<template>
  <section class="form-section">
    <h1 class="form-section__title">
      {{ $t('FINANCE.SETTLEMENT_SETTINGS.TITLE') }}
    </h1>

    <div class="row align-center">
      <div class="small-12 large-5 columns">
        <p
          v-dompurify-html="$t('FINANCE.SETTLEMENT_SETTINGS.DESCRIPTION')"
          class="form-section__description"
        />
      </div>

      <div class="small-12 large-7 columns">
        <Card v-loading="isProcessing" no-padding>
          <ElForm
            ref="settlementSettings"
            class="settlement-settings"
            :model="settlementSettings"
            :rules="rules"
            label-position="top"
            @submit.native.prevent="updateSettlementSettings"
          >
            <div class="card__block">
              <ElFormItem prop="payment_info" required>
                <template #label>
                  {{ $t('SHEBA_NUMBER') }}
                  <Chip
                    v-if="shebaStatus"
                    class="settlement-settings__sheba-badge"
                    :type="shebaStatusChipType"
                  >
                    {{ $t(shebaStatusChipText) }}
                  </Chip>
                </template>
                <ElInput
                  v-model="settlementSettings.payment_info"
                  dir="ltr"
                  name="payment_info"
                  :disabled="isProcessing"
                />
                <p class="el-form-item__help text-latin">
                  {{ $t('SHEBA_HELP_TEXT') }}
                </p>
              </ElFormItem>

              <ElFormItem>
                <ElSwitch
                  v-model="settlementSettings.automatic_settlement"
                  :disabled="isProcessing"
                />
                <span>{{ $t('AUTO_SETTLE') }}</span>
              </ElFormItem>

              <ErrorsList
                v-if="settlementSettingsFormErrors"
                :errors="settlementSettingsFormErrors"
              />

              <ElFormItem class="text-warning">
                <div class="text-bold">{{ $t('ATTENTION') }}</div>
                <span>{{ $t('FINANCE.SETTLEMENT_SETTINGS.WARNING') }}</span>
              </ElFormItem>

              <ElButton
                type="primary"
                native-type="submit"
                size="large"
                :disabled="isProcessing"
              >
                {{ $t('SAVE_CHANGES') }}
              </ElButton>
            </div>
          </ElForm>
        </Card>
      </div>
    </div>
  </section>
</template>

<script>
import { SettlementSettings } from '@/api/api';

import ErrorsList from '@@/common/ErrorsList';
import Chip from '@@/common/Chip';

import form from '@/mixins/form';
import Card from '@/components/common/Card.vue';

export default {
  name: 'SettlementSettings',

  components: {
    ErrorsList,
    Chip,
    Card,
  },

  mixins: [form],

  data() {
    return {
      settlementSettings: {
        automatic_settlement: false,
        payment_info: '',
      },
      isProcessing: false,
      settlementSettingsFormErrors: null,
    };
  },

  computed: {
    shebaStatus() {
      return this.$auth.user.publisher?.ibanStatus;
    },

    shebaStatusChipType() {
      if (this.shebaStatus === 'T') return 'success';
      if (this.shebaStatus === 'F') return 'fail';
      return 'warning';
    },

    shebaStatusChipText() {
      if (this.shebaStatus === 'T')
        return 'FINANCE.SETTLEMENT_SETTINGS.SHEBA.ACCEPTED';
      if (this.shebaStatus === 'F')
        return 'FINANCE.SETTLEMENT_SETTINGS.SHEBA.REJECTED';
      return 'FINANCE.SETTLEMENT_SETTINGS.SHEBA.PENDING';
    },

    /**
     * returns the validation rules object
     * @returns {object}
     */
    rules() {
      const valueError = this.$t('SHEBA_NUMBER_ERROR');
      return {
        payment_info: [
          {
            pattern: /^(|IR[0-9]{24})$/,
            trigger: 'change',
            message: valueError,
          },
          {
            validator: this.validatePaymentInfo,
          },
        ],
      };
    },
  },

  created() {
    this.getSettlementSettings();
  },

  methods: {
    /**
     * SettlementSettings
     *
     * takes a computed property containing specific props/values and
     * updates the user's data through a PATCH request
     *
     */
    updateSettlementSettings() {
      // first things first:
      // validate the form

      if (!this.isValid('settlementSettings')) {
        return false;
      }

      this.isProcessing = true;

      return SettlementSettings.update({}, this.settlementSettings)
        .then(({ body: response }) => {
          const $showMessage =
            this.$message[response.type || 'success'] || (() => {});
          $showMessage(response.message);
          this.settlementSettingsFormErrors = null;
        })
        .catch(({ body: { message, errors } = {} }) => {
          if (message) {
            this.$message.error(message);
          }

          if (errors) {
            this.settlementSettingsFormErrors = [...errors];
          }
        })
        .then(() => {
          this.isProcessing = false;
        });
    },

    getSettlementSettings() {
      return SettlementSettings.get()
        .then(({ body: { settlement_settings: settlementSettings } }) => {
          this.settlementSettings = Object.assign({}, settlementSettings);
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    validatePaymentInfo(rule, value, callback) {
      const valueError = this.$t('SHEBA_NUMBER_ERROR');
      const str = `${value.substring(4)}1827${value.substring(2, 4)}`;
      const x1 = 10 ** 10 % 97;
      const x2 = (x1 * x1) % 97;
      const a = str.substring(18);
      const b = str.substring(8, 18);
      const c = str.substring(0, 8);
      const remainder = ((a % 97) + ((b * x1) % 97) + ((c * x2) % 97)) % 97;
      if (remainder !== 1) {
        callback(new Error(valueError));
      } else {
        callback();
      }
    },
  },
};
</script>
<style lang="scss">
.settlement-settings {
  input {
    font-family: $lb-body-en-font-family;
  }

  &__sheba-badge {
    margin: 0 $global-margin * 0.2;
  }
}
</style>
